import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import Button from '../../../../../ui/canon/Button';
import { CheckIcon } from 'lucide-react';

const MultipleChoiceWidget = ({
  options,
  onAnswer,
  disabled,
  allowMultiple = false,
  initialValue = null,
}) => {
  // Normalize options to handle both string[] and {label, value}[] formats
  const normalizedOptions = (options || []).map(opt => {
    if (typeof opt === 'string') {
      return { label: opt, value: opt };
    }
    // Handle {label, value} objects
    return { label: opt.label || opt.value || String(opt), value: opt.value || opt.label || String(opt) };
  });
  
  // Initialize with current value if provided
  const getInitialSelected = () => {
    if (!initialValue) return [];
    if (Array.isArray(initialValue)) return initialValue;
    return [initialValue];
  };
  
  const intl = useIntl();
  const [selected, setSelected] = useState(getInitialSelected);
  
  // Update selected when initialValue changes (e.g., modal opens with new question)
  useEffect(() => {
    setSelected(getInitialSelected());
  }, [initialValue]);

  const handleSelect = (value) => {
    if (allowMultiple) {
      const newSelected = selected.includes(value)
        ? selected.filter((s) => s !== value)
        : [...selected, value];
      setSelected(newSelected);
    } else {
      setSelected([value]);
      setTimeout(() => {
        onAnswer({ value: value });
      }, 300);
    }
  };

  const handleSubmitMultiple = () => {
    if (selected.length > 0) {
      onAnswer({ value: selected });
    }
  };

  return (
    <div className="space-y-2.5">
      <p className="text-xs text-succession-text-muted">
        {allowMultiple ? intl.formatMessage({ id: 'succession.plans.widget_multiple_choice_multiple' }) : intl.formatMessage({ id: 'succession.plans.widget_multiple_choice_single' })}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {normalizedOptions.map((option) => (
          <Button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            disabled={disabled}
            variant={selected.includes(option.value) ? 'primary' : 'outline'}
            className="h-auto py-2 px-3 text-xs font-normal"
          >
            {selected.includes(option.value) && (
              <CheckIcon className="h-3 w-3 mr-1.5" />
            )}
            {option.label}
          </Button>
        ))}
      </div>
      {allowMultiple && selected.length > 0 && (
        <Button
          onClick={handleSubmitMultiple}
          disabled={disabled}
          className="w-full text-sm"
          size="sm"
        >
          {intl.formatMessage({ id: 'succession.plans.widget_multiple_choice_continue' }, { count: selected.length })}
        </Button>
      )}
    </div>
  );
};

export default MultipleChoiceWidget;
